const WORKLET_URL = "/pcm-worklet.js";
const WORKLET_NAME = "pcm16-downsampler";

export interface PcmSourceHandlers {
  // Forward one 16kHz PCM16 chunk into the transcription pipeline.
  emit: (chunk: ArrayBuffer) => void;
  // Resolves once the transcription socket is open. Finite sources should wait
  // on it before emitting so no audio is lost ahead of the stream; the mic
  // ignores it (pre-open chunks are simply dropped, as before).
  whenReady: Promise<void>;
  // Called once a finite source is exhausted. The mic never ends.
  onEnded?: () => void;
}

/**
 * A producer of 16kHz PCM16 chunks for the transcription pipeline — the live
 * mic, or (in dev) a replayed fixture. The hook is otherwise source-agnostic:
 * everything downstream of `emit` is identical regardless of where audio
 * originates.
 */
export interface PcmSource {
  start(handlers: PcmSourceHandlers): Promise<void>;
  stop(): void;
}

function micErrorMessage(e: unknown): string {
  const name = e instanceof DOMException ? e.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access is blocked. Allow mic permission in your browser, then try again.";
    case "NotFoundError":
      return "No microphone found. Connect a mic and try again.";
    case "NotReadableError":
      return "Your microphone is in use by another app. Close it and try again.";
    default:
      return "I'm not picking up any audio. Check that your mic is connected and try again.";
  }
}

export function createMicSource(): PcmSource {
  let ctx: AudioContext | null = null;
  let stream: MediaStream | null = null;
  let worklet: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;

  return {
    async start({ emit }) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
      } catch (e) {
        throw new Error(micErrorMessage(e));
      }
      ctx = new AudioContext();
      await ctx.audioWorklet.addModule(WORKLET_URL);
      source = ctx.createMediaStreamSource(stream);
      worklet = new AudioWorkletNode(ctx, WORKLET_NAME);
      worklet.port.onmessage = (ev) => emit(ev.data as ArrayBuffer);
      source.connect(worklet);
    },
    stop() {
      worklet?.disconnect();
      source?.disconnect();
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close().catch(() => {});
      worklet = source = stream = ctx = null;
    },
  };
}
